import { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, Loader, Smartphone, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authenticateWithWallet, isWalletAvailable, isMobile } from './walletService';
import { sendEmailSignInLink } from './emailAuthService';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const WalletConnectModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [connectingMethod, setConnectingMethod] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Email sign-in state
  const [signInMode, setSignInMode] = useState('select'); // 'select', 'wallet', 'email'
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Load Turnstile script and render widget (only when not in 'select' mode and not showing emailSent)
  useEffect(() => {
    // Only render Turnstile when modal is open AND user has selected a sign-in method AND not showing email sent confirmation
    if (!isOpen || signInMode === 'select' || emailSent) return;

    let checkInterval = null;

    const renderWidget = () => {
      if (!turnstileRef.current || !window.turnstile || turnstileWidgetId.current) return;

      if (!TURNSTILE_SITE_KEY) {
        console.error('Turnstile site key is not configured');
        return;
      }

      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => {
            setTurnstileToken(token);
          },
          'expired-callback': () => {
            setTurnstileToken(null);
          },
          'error-callback': (error) => {
            console.error('Turnstile error:', error);
            setError('Security verification failed. Please refresh and try again.');
            setTurnstileToken(null);
          },
          theme: 'light',
        });
      } catch (err) {
        console.error('Failed to render Turnstile widget:', err);
      }
    };

    // Load Turnstile script if not already loaded
    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (!window.turnstile && !existingScript) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => renderWidget();
      script.onerror = (err) => {
        console.error('Failed to load Turnstile script:', err);
      };
      document.head.appendChild(script);
    } else if (window.turnstile) {
      renderWidget();
    } else {
      // Script is loading, wait for it
      checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          checkInterval = null;
          renderWidget();
        }
      }, 100);
    }

    return () => {
      // Clear polling interval if active
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      // Cleanup widget when mode changes or modal closes
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (err) {
          console.error('Failed to remove Turnstile widget:', err);
        }
        turnstileWidgetId.current = null;
      }
      setTurnstileToken(null);
    };
  }, [isOpen, signInMode, emailSent]);

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
      setTurnstileToken(null);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSignInMode('select');
      setEmail('');
      setEmailSent(false);
      setError('');
    }
  }, [isOpen]);

  // Handle email sign-in
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security verification.');
      return;
    }

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setConnectingMethod('email');

    try {
      const result = await sendEmailSignInLink(email);

      if (result.success) {
        setEmailSent(true);
      } else {
        setError(result.error || 'Failed to send sign-in link.');
        resetTurnstile();
      }
    } catch (err) {
      console.error('Email sign-in error:', err);
      setError('Failed to send sign-in link. Please try again.');
      resetTurnstile();
    } finally {
      setLoading(false);
      setConnectingMethod(null);
    }
  };

  // Go back to method selection
  const handleBack = () => {
    setSignInMode('select');
    setEmail('');
    setEmailSent(false);
    setError('');
    setAgreedToTerms(false);
    setTurnstileToken(null);
  };

  if (!isOpen) return null;

  const handleConnect = async (method) => {
    setError('');
    setConnectingMethod(method);

    // Check if user agreed to terms
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service to continue.');
      setConnectingMethod(null);
      return;
    }

    // Check for Turnstile verification
    if (!turnstileToken) {
      setError('Please complete the security verification.');
      setConnectingMethod(null);
      return;
    }

    setLoading(true);

    try {
      // Check if MetaMask is available when using metamask method
      if (method === 'metamask' && !isWalletAvailable()) {
        setError('No Web3 wallet detected. Please install MetaMask or use WalletConnect.');
        setLoading(false);
        setConnectingMethod(null);
        return;
      }

      // Authenticate with selected method, passing Turnstile token
      const result = await authenticateWithWallet(method, true, turnstileToken);

      // Success - call parent callback
      onSuccess(result);
    } catch (err) {
      console.error('Connect error:', err);

      // Reset Turnstile on error so user can try again
      resetTurnstile();

      if (err.message) {
        setError(err.message);
      } else if (err.code === 4001) {
        setError('Connection request rejected. Please try again.');
      } else if (err.code === -32002) {
        setError('Connection request pending. Please check your wallet extension.');
      } else {
        setError('Failed to connect wallet. Please try again.');
      }
    } finally {
      setLoading(false);
      setConnectingMethod(null);
    }
  };

  const handleInstallWallet = () => {
    window.open('https://metamask.io/download/', '_blank');
  };

  const walletAvailable = isWalletAvailable();

  // Determine header title based on mode
  const getHeaderTitle = () => {
    if (signInMode === 'email') return 'Sign In with Email';
    if (signInMode === 'wallet') return 'Connect Your Wallet';
    return 'Sign In';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            {signInMode !== 'select' && (
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700 transition p-1 -ml-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-800">{getHeaderTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-5 space-y-3 overflow-y-auto">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-300 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Sign-in Options */}
          <div className="space-y-3">
            {/* Mode: Select sign-in method */}
            {signInMode === 'select' && (
              <>
                <div className="text-sm font-medium text-gray-700 text-center">
                  Choose Sign-in Method:
                </div>

                {/* Wallet Option */}
                <button
                  onClick={() => setSignInMode('wallet')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-3 shadow-md"
                >
                  <Smartphone className="w-6 h-6" />
                  <div className="text-left">
                    <div className="font-semibold">Connect Wallet</div>
                    <div className="text-xs text-blue-100">MetaMask, WalletConnect, etc.</div>
                  </div>
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-sm text-gray-500">or</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>

                {/* Email Option */}
                <button
                  onClick={() => setSignInMode('email')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-3 shadow-md"
                >
                  <Mail className="w-6 h-6" />
                  <div className="text-left">
                    <div className="font-semibold">Sign In with Email</div>
                    <div className="text-xs text-green-100">No wallet needed</div>
                  </div>
                </button>
              </>
            )}

            {/* Terms Agreement and Captcha - shown for both wallet and email modes */}
            {signInMode !== 'select' && !emailSent && (
              <>
                {/* Terms Agreement */}
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <div className="space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        I agree to the{' '}
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Terms of Service
                        </a>
                        {' '}and understand the terms there
                      </span>
                    </label>
                  </div>
                </div>

                {/* Turnstile Security Verification */}
                <div className="flex justify-center">
                  <div ref={turnstileRef}></div>
                </div>
              </>
            )}

            {/* Mode: Wallet connection */}
            {signInMode === 'wallet' && (
              <>
                {isMobile() ? (
                  // Mobile: Show WalletConnect only
                  <>
                    <button
                      onClick={() => handleConnect('walletconnect')}
                      disabled={loading || !agreedToTerms || !turnstileToken}
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium disabled:opacity-50 transition flex items-center justify-center gap-2 shadow-md"
                    >
                      {loading && connectingMethod === 'walletconnect' ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Opening Wallet...
                        </>
                      ) : (
                        <>
                          <Smartphone className="w-5 h-5" />
                          <span>Connect Wallet</span>
                        </>
                      )}
                    </button>
                    <div className="text-xs text-blue-600 bg-blue-50 p-3 rounded-lg text-center">
                      Works with MetaMask, Trust Wallet, Rainbow, Coinbase Wallet, and 200+ other wallets!
                    </div>
                  </>
                ) : (
                  // Desktop: Show both options
                  <>
                    {/* Browser Wallet Option */}
                    {walletAvailable ? (
                      <button
                        onClick={() => handleConnect('metamask')}
                        disabled={loading || !agreedToTerms || !turnstileToken}
                        className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-medium disabled:opacity-50 transition flex items-center justify-center gap-2 shadow-md"
                      >
                        {loading && connectingMethod === 'metamask' ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <span className="text-xl">🦊</span>
                            <span>MetaMask Browser Extension</span>
                            <span className="text-xs bg-green-400/30 text-green-100 px-2 py-0.5 rounded-full">Installed</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={handleInstallWallet}
                        className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2 shadow-md"
                      >
                        <span className="text-xl">🦊</span>
                        <span>Install MetaMask Extension</span>
                      </button>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <span className="text-sm text-gray-500">Or</span>
                      <div className="flex-1 h-px bg-gray-300"></div>
                    </div>

                    {/* WalletConnect Option */}
                    <button
                      onClick={() => handleConnect('walletconnect')}
                      disabled={loading || !agreedToTerms || !turnstileToken}
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium disabled:opacity-50 transition flex items-center justify-center gap-2 shadow-md"
                    >
                      {loading && connectingMethod === 'walletconnect' ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Opening QR Code...
                        </>
                      ) : (
                        <>
                          <Smartphone className="w-5 h-5" />
                          <span>Scan QR with Mobile Wallet</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </>
            )}

            {/* Mode: Email sign-in */}
            {signInMode === 'email' && (
              <>
                {emailSent ? (
                  // Email sent success message
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Check Your Email</h3>
                      <p className="text-sm text-gray-600">
                        We've sent a sign-in link to:
                      </p>
                      <p className="text-sm font-medium text-gray-800 mt-1">{email}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                      <p>Click the link in your email to complete sign-in.</p>
                      <p className="mt-2 text-xs text-blue-600">
                        The link will expire in 1 hour. Check your spam folder if you don't see it.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setEmailSent(false);
                        setEmail('');
                        // useEffect will handle cleanup and re-render of Turnstile widget
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Use a different email
                    </button>
                  </div>
                ) : (
                  // Email input form
                  <form onSubmit={handleEmailSignIn} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || !agreedToTerms || !turnstileToken || !email}
                      className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white rounded-lg font-medium disabled:opacity-50 transition flex items-center justify-center gap-2 shadow-md"
                    >
                      {loading && connectingMethod === 'email' ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          <span>Send Sign-in Link</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      We'll send you a magic link to sign in. No password needed!
                    </p>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletConnectModal;
